import { FC } from "react";
import { Link, Stack } from "@mui/material";
import { BodyText, HeadingText1, HeadingText2 } from "../styles/styledComponents";
import ScreenBase from "./ScreenBase";

type Props = {};

const YouTubeContentIdClaimScreen: FC<Props> = () => {
	return (
		<ScreenBase
			scrollableChildren={(
				<Stack component="section" spacing={4}>
					<HeadingText1>YouTube Content ID Claim?</HeadingText1>
					<BodyText>
						If you've received a YouTube copyright claim in a video that contains my music,
						then you can use this guide to find out how to resolve the issue.
					</BodyText>
					<HeadingText2>Why did I get a copyright claim?</HeadingText2>
					<Stack spacing={2}>
						<BodyText>
							Tim Kulig Free Music is available, well, FOR FREE! ~ Under Creative Commons: By
							Attribution 4.0.  This means that, so long as you give me proper attribution in
							your video description, my music is free to use.  However, it is difficult to
							enforce this license across social media. There are also many instances where
							third parties claim other composer's music, causing widespread copyright claims
							on videos legally using that music &ndash; which can be really difficult to resolve.
						</BodyText>
						<BodyText>
							To counter these issues and get ahead of this problem so many of my peers have
							been experiencing, I have implemented a "Smart Content ID" system to protect
							content creators and my catalog of music from unauthorized uses on YouTube. This
							system scans the description of videos using my music looking for a credit. If
							no credit is found, the system places a copyright claim on the video.
						</BodyText>
						<BodyText>
							If you've received a claim, it is possible you do not have a credit in the
							description of your video.
						</BodyText>
					</Stack>
					<HeadingText2>What does a claim from Tim Kulig Free Music look like?</HeadingText2>
					<Stack spacing={2}>
						<BodyText>
							A claim from me will state ownership by the following:
						</BodyText>
						<BodyText fontStyle="italic" align="center" sx={{ color: 'white' }}>
							We Are Era Music BV, claimed on behalf of Tim Kulig
						</BodyText>
						<BodyText>
							If the claim looks different to this, then your claim may be because a different
							reason, including a conflict with a third-party who is claiming my music falsely.
							If this is the case, please get in touch here:
						</BodyText>
						<BodyText align="center">
							<Link href="mailto:timkuligfreemusic@gmail.com" style={{ opacity: 1}}>timkuligfreemusic@gmail.com</Link>
						</BodyText>
					</Stack>
					<HeadingText2>How do I remove the claim?</HeadingText2>
					<Stack spacing={1}>
						<BodyText sx={{ pl: 4 }}>
							1. Make sure your video description has the correct attribution language (see below)
						</BodyText>
						<BodyText sx={{ pl: 4 }}>
							2. Fill out this form to have the claim released:
						</BodyText>
						<BodyText align="center">
							<Link href="https://forms.gle/rcTwtsunKoFKbpPJ6">&gt;&gt; Claim Release Form &lt;&lt;</Link>
						</BodyText>
					</Stack>
					<HeadingText2>What should a credit look like?</HeadingText2>
					<Stack spacing={2}>
						<Stack spacing={1}>
							<BodyText>
								The Smart Content ID system is specifically looking for the following text in your video description:
							</BodyText>
							<BodyText>
								<ul>
									<li style={{ marginBottom: 8 }}>
										Song(s) Name / Title
									</li>
									<li>
										Tim Kulig / timkulig.com
									</li>
								</ul>
							</BodyText>
						</Stack>
						<BodyText>
							However, your credit should at least say that it is the music that you are
							crediting me for (e.g. Music by Tim Kulig) and the song title (so others who
							resonate with the track can find it).  An <strong style={{ fontStyle: 'italic' }}>ideal</strong> credit will look like the
							following:
						</BodyText>
						<BodyText sx={{ pl: 4, color: 'white' }}>
							Song Title(s):<br />
							Tim Kulig (timkulig.com)<br />
							Licensed under Creative Commons: By Attribution 4.0<br />
							<Link href="https://creativecommons.org/licenses/by/4.0/">creativecommons.org/licenses/by/4.0/</Link>
						</BodyText>
					</Stack>
					<HeadingText2>I gave you credit &ndash; why did I still get a claim?</HeadingText2>
					<Stack spacing={2}>
						<BodyText>
							Some possible reasons you got a claim:
						</BodyText>
						<Stack spacing={1}>
							<BodyText sx={{ pl: 4 }}>
								<ul>
									<li style={{ marginBottom: 8 }}>
										The credit was added after the claim was made. Adding credits
										will not automatically remove a claim. Please use
										the <Link href="https://forms.gle/rcTwtsunKoFKbpPJ6">form</Link> above to request removal.
									</li>
									<li style={{ marginBottom: 8 }}>
										The claim was added for a <strong>different reason</strong> (related
										to a third-party issue) 
									</li>
									<li>
										The credit has been <strong>misspelled</strong>, particularly my name:
										Tim Kulig, or the track titles.
									</li>
								</ul>
							</BodyText>
						</Stack>
						<Stack spacing={1}>
							<BodyText>
								If you've corrected the credit, you can then use this form to have the claim released:
							</BodyText>
							<BodyText align="center" sx={{ color: 'white' }}>
								<Link href="https://forms.gle/rcTwtsunKoFKbpPJ6">&gt;&gt; Claim Release Form &lt;&lt;</Link>
							</BodyText>
						</Stack>
						<BodyText>
							If the claim is still present after going through the processes above,
							please <Link href="mailto:timkuligfreemusic@gmail.com">get in contact</Link> with me
							directly to resolve the issue.
						</BodyText>
					</Stack>
				</Stack>
			)}
		/>
	);
};

export default YouTubeContentIdClaimScreen;